/**
 * useBridgeInsightStore — top-level signal store for bridge + cross-chain
 * findings on the currently-selected node.
 *
 * Purpose: IntelligenceTab loads bridge / cross-chain data per node. Other
 * components (TriageRibbon, future algorithms, dashboards) need the summary
 * to render top-level chips without re-fetching. This store is the shared
 * surface.
 *
 * IntelligenceTab writes on load; TriageRibbon reads.
 *
 * Keyed by `${chain}:${address}` so multiple nodes on the canvas don't
 * stomp each other when the user switches selection quickly.
 */
import { create } from 'zustand';

export interface BridgeInsight {
    /** Number of bridge interactions detected on this node's txs */
    bridge_count: number;
    /** Primary bridge family (LayerZero, Stargate, etc.) — first match */
    primary_bridge_family?: string | null;
    /** Last-known target chain if a specific one was identified */
    primary_target_chain?: string | null;
}

export interface CrossChainInsight {
    /** Total chains discovered by GoldRush */
    total_chains: number;
    /** Chains we have fetcher support for (can drill into) */
    supported_count: number;
    /** Top 3 chain names for compact display on the ribbon */
    preview_chains: string[];
}

interface BridgeInsightState {
    bridges: Record<string, BridgeInsight | null>;
    crossChain: Record<string, CrossChainInsight | null>;

    setBridge: (chain: string, address: string, data: BridgeInsight | null) => void;
    setCrossChain: (chain: string, address: string, data: CrossChainInsight | null) => void;
    getBridge: (chain: string, address: string) => BridgeInsight | null;
    getCrossChain: (chain: string, address: string) => CrossChainInsight | null;
    clearAll: () => void;
}

const _key = (chain: string, address: string) => `${chain}:${address}`;

export const useBridgeInsightStore = create<BridgeInsightState>((set, get) => ({
    bridges: {},
    crossChain: {},

    setBridge: (chain, address, data) => {
        set(s => ({ bridges: { ...s.bridges, [_key(chain, address)]: data } }));
    },
    setCrossChain: (chain, address, data) => {
        set(s => ({ crossChain: { ...s.crossChain, [_key(chain, address)]: data } }));
    },
    getBridge: (chain, address) => get().bridges[_key(chain, address)] || null,
    getCrossChain: (chain, address) => get().crossChain[_key(chain, address)] || null,
    clearAll: () => set({ bridges: {}, crossChain: {} }),
}));
